/**
 * ToastContainer - renders active toasts from the toast store.
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useToastStore } from '../../stores/toast-store';

const TYPE_STYLES = {
  success: { bg: 'var(--bg-elevated)', border: 'var(--success)' },
  error: { bg: 'var(--bg-elevated)', border: 'var(--error)' },
  warning: { bg: 'var(--bg-elevated)', border: 'var(--warning)' },
  info: { bg: 'var(--bg-elevated)', border: 'var(--accent)' },
} as const;

type ToastStyleKey = keyof typeof TYPE_STYLES;

function ToastTypeIcon({ type, color }: { type: ToastStyleKey; color: string }) {
  const common = {
    width: 14,
    height: 14,
    viewBox: '0 0 16 16',
    fill: 'none',
    stroke: color,
    strokeWidth: 1.8,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  };

  if (type === 'success') {
    return (
      <svg {...common}>
        <path d="M3.5 8.5l3 3 6-7" />
      </svg>
    );
  }

  if (type === 'error') {
    return (
      <svg {...common}>
        <path d="M4.5 4.5l7 7M11.5 4.5l-7 7" />
      </svg>
    );
  }

  if (type === 'warning') {
    return (
      <svg {...common} viewBox="0 0 18 16">
        <path d="M9 1.8L16.2 14H1.8L9 1.8z" />
        <path d="M9 6v3.5" />
        <path d="M9 12.2h.01" />
      </svg>
    );
  }

  return (
    <svg {...common}>
      <circle cx="8" cy="8" r="6" />
      <path d="M8 7.2v4" />
      <path d="M8 4.8h.01" />
    </svg>
  );
}

export function ToastContainer() {
  const toasts = useToastStore((s) => s.toasts);
  const removeToast = useToastStore((s) => s.removeToast);

  return (
    <div
      className="fixed bottom-6 right-6 z-50 flex flex-col gap-2"
      style={{ pointerEvents: 'none' }}
    >
      <AnimatePresence>
        {toasts.map((toast) => {
          const style = TYPE_STYLES[toast.type] ?? TYPE_STYLES.info;
          return (
            <motion.div
              key={toast.id}
              initial={{ opacity: 0, y: 16, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.95 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="flex items-center gap-3 px-4 py-3 rounded-lg max-w-sm"
              style={{
                backgroundColor: style.bg,
                border: `1px solid ${style.border}`,
                boxShadow: 'var(--shadow-lg)',
                pointerEvents: 'auto',
              }}
            >
              <span
                className="shrink-0 flex items-center justify-center"
                style={{ color: style.border }}
              >
                <ToastTypeIcon type={toast.type ?? 'info'} color={style.border} />
              </span>
              <span
                className="text-sm flex-1"
                style={{ color: 'var(--text-primary)', fontFamily: 'var(--font-body)' }}
              >
                {toast.message}
                {toast.countdown !== undefined && toast.countdown > 0
                  ? ` (${toast.countdown}s)`
                  : ''}
              </span>
              <button
                onClick={() => removeToast(toast.id)}
                className="shrink-0 w-5 h-5 flex items-center justify-center rounded text-xs"
                style={{ color: 'var(--text-tertiary)', backgroundColor: 'transparent', border: 'none', cursor: 'pointer' }}
                aria-label="Dismiss"
              >
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
                  <path d="M2 2L10 10M10 2L2 10" />
                </svg>
              </button>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}
